import { defineStore } from 'pinia'
import { readonly, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { UIActionPermission } from '@/types'
import logging from '@/utils/logging.ts'

export enum SpecialPermissions {
  ProzessStarten = '_prozess_starten'
}

export type Permission = {
  view: boolean
  add: boolean
  change: boolean
  delete: boolean
  parentview: boolean
}

export const usePermissionsStore = defineStore('permissions', () => {
  const router = useRouter()
  const route = useRoute()

  // TODO: vielleicht hier ein shallowRef für bessere Performance? Testen ob es zu Problemen kommt und ob Performance besser ist
  const permissions = ref<Record<string, Permission>>({})
  const permissionDeniedField = ref<string>('')

  const assignComplexPermissions = (payload: Record<string, Permission>) => {
    payload[SpecialPermissions.ProzessStarten] = {
      add: (payload['aufgaben.aufgaben']?.add ?? false) && (payload['aufgaben.prozessvorlagen']?.view ?? false),
      // dummy, never relevant
      view: true,
      change: false,
      delete: false,
      parentview: false
    }
    // sollte es in Zukunft mehrere komplexe Rechte geben, können sie hier hinzugefügt werden
  }

  const setPermissions = (payload: Record<string, Permission>) => {
    if (payload) {
      assignComplexPermissions(payload)
      permissions.value = payload
    } else {
      logging.error('setPermissions(): payload is empty')
      permissions.value = {}
    }
  }

  const setPermissionDeniedField = (payload: string) => {
    permissionDeniedField.value = payload
  }

  const redirectToPermissionDenied = async () => {
    return router.replace({ name: 'PermissionDenied', query: { failedUrl: route.fullPath } })
  }

  const getPermissions = readonly(permissions)
  const getPermissionDeniedField = readonly(permissionDeniedField)

  const hasViewPermission = (permissionName: string) => {
    return permissions.value[permissionName]?.view ?? false
  }
  const hasAddPermission = (permissionName: string) => {
    return permissions.value[permissionName]?.add ?? false
  }
  const hasChangePermission = (permissionName: string) => {
    return permissions.value[permissionName]?.change ?? false
  }
  const hasDeletePermission = (permissionName: string) => {
    return permissions.value[permissionName]?.delete ?? false
  }
  const hasParentviewPermission = (permissionName: string) => {
    return permissions.value[permissionName]?.parentview ?? false
  }

  const hasActionPermission = (permission?: UIActionPermission) => {
    if (permission) {
      if (permission.action === 'view') {
        return hasViewPermission(permission.name)
      } else if (permission.action === 'add') {
        return hasAddPermission(permission.name)
      } else if (permission.action === 'change') {
        return hasChangePermission(permission.name)
      } else if (permission.action === 'delete') {
        return hasDeletePermission(permission.name)
      }
    }
    return true
  }

  return {
    setPermissions, setPermissionDeniedField, redirectToPermissionDenied,
    getPermissions, getPermissionDeniedField,
    hasViewPermission, hasAddPermission, hasChangePermission, hasDeletePermission,
    hasParentviewPermission, hasActionPermission
  }
})